import Preferences from './application/Preferences.js';
/**
 */
class PreferencesManager {
    constructor() {
        this.preferences = new Preferences();
        const ready = {};
        // Configure a promise to use for "ready" indication
        const promise = new Promise((resolve, reject) => {
            ready.resolve = resolve;
            ready.reject = reject;
        });
        ready.promise = promise;
        this._ready = ready;
        this.chain = Promise.resolve(this);
    }
    /**
     * @param {string} preferenceName
     * @param {*} fallback
     * @param {string} [collectionName]
     * @return {*}
     */
    get(preferenceName, fallback = null, collectionName = 'general') {
        const preference = (this.has(preferenceName, collectionName)
            ? this.preferences[collectionName][preferenceName]
            : fallback);
        return preference;
    }
    /**
     * @param {string} preferenceName
     * @param {string} [collectionName]
     * @return {boolean}
     */
    has(preferenceName, collectionName = 'general') {
        return preferenceName in this.preferences[collectionName];
    }
    /**
     * @param {string} preferenceName
     * @param {*} preference
     * @param {string} [collectionName]
     * @return {Promise<PreferencesManager>}
     */
    async set(preferenceName, preference, collectionName = 'general') {
        // Update "local" reference
        this.preferences[collectionName][preferenceName] = preference;
        return save(this);
    }
    /**
     * Batch update a series of preferences as a single call.
     *
     * @param {object} updated
     * @param {string} [collectionName]
     * @return {Promise<PreferencesManager>}
     */
    async assign(updated, collectionName = 'general') {
        // Update "local" references
        const existing = this.preferences[collectionName];
        this.preferences[collectionName] = { ...existing, ...updated };
        return save(this);
    }
    /**
     * @param {ApplicationUser} user
     * @return {Promise<PreferencesManager>}
     */
    async loadFor(user) {
        return Preferences.forUser(user)
            .then((preferences) => {
            this.preferences = preferences;
            this._ready.resolve(this);
            return this;
        });
    }
    /**
     * @return {Promise<PreferencesManager>}
     */
    async ready() {
        return this._ready.promise;
    }
    /**
     * @param {object} environment
     * @param {string} environment.domain
     * @param {string} environment.protocol
     */
    static configure({ domain, protocol }) {
        PreferencesManager.Preferences.$AR.url = `${protocol}://data.${domain}/api/dashboard/v1/user-preferences`;
    }
}
PreferencesManager.Preferences = Preferences;
/**
 * Copy the preferences object to ensure we don't pass a reference.
 *
 * @param {object} preferences
 * @return {object}
 */
function copy(preferences) {
    return JSON.parse(JSON.stringify(preferences));
}
/**
 * Add a new "link" to the chain of sequential changes. The saved record is not
 * connected to the "live" one to prevent reverting queued changes.
 *
 * @param {PreferencesManager} preferencesManager
 * @return {Promise<PreferencesManager>}
 */
function save(preferencesManager) {
    // Make a copy for making this single request
    const { id, general, info, hubspot, user } = preferencesManager.preferences;
    const resource = new Preferences(copy({ id, general, info, hubspot, user }));
    // Add to the sequential changes; read/write ID from response
    preferencesManager.chain = (preferencesManager.chain
        .then(() => {
        resource.id = preferencesManager.preferences.id;
        return resource.save(undefined, undefined);
    })
        .then(() => {
        preferencesManager.preferences.id = resource.id;
        return preferencesManager;
    }));
    return preferencesManager.chain;
}
export default PreferencesManager;
